import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { publishedService, revisionService, approvalService } from "../../services/firestoreService";
import { auth } from "../../firebase";
import { db } from "../../firebase";
import { doc, writeBatch } from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  FiPlus,
  FiCalendar,
  FiBold,
  FiItalic,
  FiUnderline,
  FiType,
  FiList,
  FiLink,
  FiImage,
  FiVideo,
  FiMoreHorizontal,
  FiFileText,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiAlignJustify,
  FiPackage,
  FiArrowLeft,
  FiRotateCcw,
  FiRotateCw,
  FiCode,
  FiX,
  FiChevronDown,
  FiMinus,
  FiVolume2,
  FiUpload
} from "react-icons/fi";
import Portal from "../../components/Portal";
import "./staffcss/contentdashboard.css";

const AdminContentRevision = () => {
  const { contentId } = useParams();
  const navigate = useNavigate();
  
  const [content, setContent] = useState("");
  const [debouncedContent, setDebouncedContent] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState("");
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [selectedStyle, setSelectedStyle] = useState("Normal text");
  const [embedBar, setEmbedBar] = useState(null); // { type, url, text, cursorOffset }
  const [alignment, setAlignment] = useState("left");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop"); // 'mobile' | 'desktop'
  const [publishCategory, setPublishCategory] = useState("");
  const [minorEligible, setMinorEligible] = useState(false);
  const [isEditSocialOpen, setIsEditSocialOpen] = useState(false);
  const [socialTitle, setSocialTitle] = useState("");
  const [socialDescription, setSocialDescription] = useState("");
  const [socialImageUrl, setSocialImageUrl] = useState("");
  const [characterCount, setCharacterCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [originalContent, setOriginalContent] = useState(null);
  const [revisionData, setRevisionData] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successAction, setSuccessAction] = useState(null); // 'applied' or 'rejected'
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'apply' or 'reject'
  const [isExecutingAction, setIsExecutingAction] = useState(false); // Loading state for confirm modal
  const [cleanContent, setCleanContent] = useState(""); // Content without highlighting
  
  const editorRef = useRef(null);
  const cursorRestoreRef = useRef(null);
  const contentUpdateSourceRef = useRef(null); // 'user' | 'programmatic' – only replace editor DOM when programmatic
  const importFileInputRef = useRef(null);
  const socialImageInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const historyTimerRef = useRef(null);

  const PUBLISH_CATEGORIES = [
    "Menstrual Wellness and Hygiene",
    "Contraception",
    "Fertility Basics",
    "STI Awareness",
    "Mental Health in Reproductive Care",
    "Male Reproductive Health",
    "Pregnancy Prevention",
    "General Reproductive Wellness",
    "Community Outreach Programs"
  ];

  useEffect(() => {
    fetchData();
  }, [contentId]);

  // Apply highlighting when content is loaded
  useEffect(() => {
    if (debouncedContent && originalContent) {
      const highlightedHtml = highlightChanges(originalContent.bodyHtml || "", debouncedContent);
      if (highlightedHtml !== debouncedContent) {
        contentUpdateSourceRef.current = "programmatic";
        setContent(highlightedHtml);
        setDebouncedContent(highlightedHtml);
        setCharacterCount(calculateCharacterCount(debouncedContent));
      }
    }
  }, [debouncedContent, originalContent]);

  // Apply highlighting when editor is ready
  useEffect(() => {
    const timer = setTimeout(() => {
      applyHighlightingToEditor();
    }, 100);
    return () => clearTimeout(timer);
  }, [originalContent]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get original content from published collection
      const original = await publishedService.getPublishedByContentId(contentId);
      
      // Get revision from revisions collection
      const revision = await revisionService.getRevisionByContentId(contentId);
      
      if (original) {
        setOriginalContent(original);
      }
      
      if (revision) {
        setRevisionData(revision);
        setTitle(revision.title || "");
        setSubtitle(revision.subtitle || "");
        contentUpdateSourceRef.current = "programmatic";
        
        // Store clean content and apply highlighting
        const cleanContent = revision.bodyHtml || "";
        setCleanContent(cleanContent);
        const highlightedContent = highlightChanges(original?.bodyHtml || "", cleanContent);
        setContent(highlightedContent);
        setDebouncedContent(highlightedContent);
        setCharacterCount(calculateCharacterCount(cleanContent));
        
        setTags(revision.tags || []);
        setPublishCategory(revision.category || "General Health");
        setSocialTitle(revision.socialTitle || revision.title || "");
        setSocialDescription(revision.socialDescription || "");
        setSocialImageUrl(revision.socialImageUrl || "");
        setMinorEligible(revision.minorEligible);
      } else if (original) {
        setTitle(original.title || "");
        setSubtitle(original.subtitle || "");
        contentUpdateSourceRef.current = "programmatic";
        setContent(original.bodyHtml || "");
        setDebouncedContent(original.bodyHtml || "");
        setCharacterCount(calculateCharacterCount(original.bodyHtml || ""));
        setTags(original.tags || []);
        setPublishCategory(original.category || "General Health");
        setSocialTitle(original.socialTitle || original.title || "");
        setSocialDescription(original.socialDescription || "");
        setSocialImageUrl(original.socialImageUrl || "");
        setMinorEligible(original.minorEligible);
      }
    } catch (error) {
      alert("Failed to load content.");
    } finally {
      setLoading(false);
    }
  };

  const stripHtmlToText = (html) => {
    try {
      const div = document.createElement("div");
      div.innerHTML = html || "";
      return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
    } catch {
      return "";
    }
  };

  const calculateCharacterCount = (html) => {
    return stripHtmlToText(html).length;
  };

  const highlightChanges = (originalHtml, revisedHtml) => {
    if (!originalHtml || !revisedHtml) return revisedHtml;
    
    // Don't highlight if already highlighted
    if (revisedHtml.includes('<mark')) return revisedHtml;
    
    // Simple character-by-character diff
    const originalText = stripHtmlToText(originalHtml);
    const revisedText = stripHtmlToText(revisedHtml);
    
    if (originalText === revisedText) return revisedHtml;
    
    // Find contiguous changed segments
    let result = '';
    let i = 0, j = 0;
    let inChange = false;
    
    while (i < originalText.length && j < revisedText.length) {
      if (originalText[i] === revisedText[j]) {
        if (inChange) {
          result += '</mark>';
          inChange = false;
        }
        result += revisedText[j];
        i++;
        j++;
      } else {
        if (!inChange) {
          result += '<mark style="background-color: #dbeafe; padding: 2px 4px; border-radius: 4px; border: 1px solid #bfdbfe; color: #0c4a6e;">';
          inChange = true;
        }
        result += revisedText[j];
        j++;
      }
    }
    
    // Handle remaining characters
    if (j < revisedText.length) {
      if (!inChange) {
        result += '<mark style="background-color: #dbeafe; padding: 2px 4px; border-radius: 4px; border: 1px solid #bfdbfe; color: #0c4a6e;">';
      }
      result += revisedText.substring(j);
    }
    
    if (inChange) {
      result += '</mark>';
    }
    
    // If no changes were found, return original
    if (!result.includes('<mark')) return revisedHtml;
    
    return result;
  };

  const applyHighlightingToEditor = () => {
    if (editorRef.current && originalContent) {
      const currentHtml = editorRef.current.innerHTML;
      const highlightedHtml = highlightChanges(originalContent.bodyHtml || "", currentHtml);
      if (highlightedHtml !== currentHtml) {
        contentUpdateSourceRef.current = "programmatic";
        editorRef.current.innerHTML = highlightedHtml;
        setDebouncedContent(highlightedHtml);
        setCharacterCount(calculateCharacterCount(highlightedHtml));
      }
    }
  };

  const openEditSocial = () => {
    setIsEditSocialOpen(true);
  };

  const closeEditSocial = () => {
    setIsEditSocialOpen(false);
  };

  const handlePickSocialImage = (e) => {
    const file = e.target.files[0];
    if (!file.type?.startsWith("image/")) return;
    
    const maxSizeInBytes = 500 * 1024;
    if (file.size > maxSizeInBytes) {
      alert(`Image is too large. Please choose an image smaller than 500KB. Selected file: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      e.target.value = "";
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = String(reader.result || "");
      const estimatedSizeInBytes = Math.round(base64String.length * 0.75);
      if (estimatedSizeInBytes > 1024 * 1024) {
        alert(`Image is still too large after encoding. Please choose a smaller image or compress it first.`);
        e.target.value = "";
        return;
      }
      
      setSocialImageUrl(base64String);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const getCaretCharacterOffsetWithin = (element) => {
    if (!element || typeof element.nodeType !== "number") return { start: 0, end: 0 };
    const selection = window.getSelection();
    if (!selection.rangeCount) return { start: 0, end: 0 };
    const range = selection.getRangeAt(0);
    try {
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      const end = preCaretRange.toString().length;
      preCaretRange.setEnd(range.startContainer, range.startOffset);
      const start = preCaretRange.toString().length;
      return { start, end };
    } catch (err) {
      return { start: 0, end: 0 };
    }
  };

  const setCaretPosition = (element, startOffset, endOffset) => {
    if (!element || typeof element.nodeType !== "number") return;
    const selection = window.getSelection();
    const range = document.createRange();
    let charCount = 0;
    let startNode = null, startOffsetInNode = 0, endNode = null, endOffsetInNode = 0;

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const nextCount = charCount + node.length;
        if (startNode === null && startOffset <= nextCount) {
          startNode = node;
          startOffsetInNode = startOffset - charCount;
        }
        if (endNode === null && endOffset <= nextCount) {
          endNode = node;
          endOffsetInNode = endOffset - charCount;
        }
        charCount = nextCount;
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          walk(node.childNodes[i]);
          if (startNode !== null && endNode !== null) return;
        }
      }
    };
    walk(element);
    if (startNode === null) {
      startNode = element;
      startOffsetInNode = element.childNodes.length;
    }
    if (endNode === null) {
      endNode = element;
      endOffsetInNode = element.childNodes.length;
    }
    try {
      range.setStart(startNode, startOffsetInNode);
      range.setEnd(endNode, endOffsetInNode);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch (e) {
      try {
        range.selectNodeContents(element);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (e2) {
        // ignore if element is detached
      }
    }
  };

  const saveToHistory = () => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(debouncedContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleContentChange = (e, options) => {
    const el = e.target;
    const editor = editorRef.current;
    const newContent = editor && editor.innerHTML !== undefined ? editor.innerHTML : "";
    const isUserInput =
      el && typeof el.nodeType === "number" && editor && editor.contains(el);

    if (isUserInput && editor) {
      contentUpdateSourceRef.current = "user";
      editor.style.direction = "ltr";
      editor.style.unicodeBidi = "embed";
    } else if (!options?.preserveCursor) {
      cursorRestoreRef.current = null;
      contentUpdateSourceRef.current = "programmatic";
    }

    // Extract clean content (remove highlighting spans)
    const cleanNewContent = newContent.replace(/<span[^>]*style="[^"]*"[^>]*>([^<]*)<\/span>/gi, '$1');
    setCleanContent(cleanNewContent);
    
    // Apply highlighting for display
    const highlightedContent = highlightChanges(originalContent?.bodyHtml || "", cleanNewContent);
    setContent(highlightedContent);
    setDebouncedContent(highlightedContent);
    setCharacterCount(calculateCharacterCount(cleanNewContent));
    
    // Debounce history saves to reduce frequency (2 second delay)
    if (historyTimerRef.current) {
      clearTimeout(historyTimerRef.current);
    }
    
    historyTimerRef.current = setTimeout(() => {
      saveToHistory();
    }, 2000);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      contentUpdateSourceRef.current = "programmatic";
      const previousContent = history[historyIndex - 1];
      setContent(previousContent);
      setDebouncedContent(previousContent);
      setCharacterCount(calculateCharacterCount(previousContent));
      if (editorRef.current) {
        editorRef.current.innerHTML = previousContent;
      }
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      contentUpdateSourceRef.current = "programmatic";
      const nextContent = history[historyIndex + 1];
      setContent(nextContent);
      setDebouncedContent(nextContent);
      setCharacterCount(calculateCharacterCount(nextContent));
      if (editorRef.current) {
        editorRef.current.innerHTML = nextContent;
      }
    }
  };

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const execCommand = (command, value = null) => {
    const editor = editorRef.current;
    if (!editor) return;
    focusEditor();
    document.execCommand(command, false, value);
    contentUpdateSourceRef.current = "programmatic";
    cursorRestoreRef.current = getCaretCharacterOffsetWithin(editor);
    handleContentChange({ target: { innerHTML: editor.innerHTML } }, { preserveCursor: true });
  };

  const insertHTMLAtCursor = (html) => {
    const editor = editorRef.current;
    if (!editor) return;
    focusEditor();
    document.execCommand("insertHTML", false, html);
    contentUpdateSourceRef.current = "programmatic";
    cursorRestoreRef.current = getCaretCharacterOffsetWithin(editor);
    handleContentChange({ target: { innerHTML: editor.innerHTML } }, { preserveCursor: true });
  };

  const embedRemoveButton = '<span class="cc-embed-remove" role="button" tabindex="-1" title="Remove" aria-label="Remove">&times;</span>';

  const handleEditorClick = (e) => {
    const editor = editorRef.current;
    if (!editor) return;
    const removeBtn = e.target.closest?.(".cc-embed-remove");
    if (!removeBtn) return;
    e.preventDefault();
    e.stopPropagation();
    const embed = removeBtn.closest?.(".cc-embed");
    if (embed && editor.contains(embed)) {
      try {
        const sel = window.getSelection();
        const range = document.createRange();
        const prev = embed.previousSibling;
        if (prev) {
          range.setStartAfter(prev);
          range.collapse(true);
        } else {
          range.setStart(editor, 0);
          range.collapse(true);
        }
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (err) {
        // ignore
      }
      embed.remove();
      contentUpdateSourceRef.current = "programmatic";
      cursorRestoreRef.current = getCaretCharacterOffsetWithin(editor);
      handleContentChange({ target: { innerHTML: editor.innerHTML } }, { preserveCursor: true });
    }
  };

  const handleImportFiles = (e) => {
    const files = e.target.files;
    if (!files || !files.length || !editorRef.current) return;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const type = file.type || "";
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || "");
        if (type.startsWith("image/")) {
          insertHTMLAtCursor(
            `<figure class="cc-embed cc-embed-image" contenteditable="false">${embedRemoveButton}<img src="${dataUrl}" alt="${file.name || ""}" /><figcaption contenteditable="true">Caption</figcaption></figure>`
          );
        } else if (type.startsWith("audio/")) {
          insertHTMLAtCursor(
            `<div class="cc-embed cc-embed-audio" contenteditable="false">${embedRemoveButton}<audio controls src="${dataUrl}">Your browser does not support audio.</audio></div>`
          );
        } else if (type.startsWith("video/")) {
          insertHTMLAtCursor(
            `<div class="cc-embed cc-embed-video" contenteditable="false">${embedRemoveButton}<video controls src="${dataUrl}" style="max-width:100%;height:auto;">Your browser does not support video.</video></div>`
          );
        }
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const openEmbedBar = (type) => {
    let cursorOffset = null;
    try {
      const editor = editorRef.current;
      const sel = window.getSelection();
      if (editor && sel.rangeCount && sel.anchorNode && editor.contains(sel.anchorNode)) {
        cursorOffset = getCaretCharacterOffsetWithin(editor);
      }
    } catch (e) {
      // ignore
    }
    setEmbedBar({ type, url: "", text: "", cursorOffset });
  };

  const closeEmbedBar = () => {
    setEmbedBar(null);
  };

  const handleAlignment = (align) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const value = { left: "left", center: "center", right: "right", justify: "justify" }[align];
    if (!value) return;
    const cmd = { left: "justifyLeft", center: "justifyCenter", right: "justifyRight", justify: "justifyFull" }[align];
    document.execCommand(cmd, false, null);
    try {
      const sel = window.getSelection();
      if (sel.rangeCount && sel.anchorNode && editor.contains(sel.anchorNode)) {
        let node = sel.getRangeAt(0).commonAncestorContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
        const block = node.closest ? node.closest("p, h1, h2, h3, h4, h5, h6, div") : null;
        if (block && block !== editor && editor.contains(block)) {
          block.style.textAlign = value;
        }
      }
    } catch (e) {
      // ignore
    }
    setAlignment(align);
    const ed = editorRef.current;
    if (ed) {
      contentUpdateSourceRef.current = "programmatic";
      cursorRestoreRef.current = getCaretCharacterOffsetWithin(ed);
      handleContentChange({ target: { innerHTML: ed.innerHTML } }, { preserveCursor: true });
    }
  };

  const insertEmbed = () => {
    if (!embedBar || !editorRef.current) return;
    const { type, url, text, cursorOffset } = embedBar;
    const trimmedUrl = (url || "").trim();
    if (!trimmedUrl && type !== "comment") return;

    const editor = editorRef.current;
    if (cursorOffset != null) {
      const len = editor.textContent?.length ?? 0;
      setCaretPosition(editor, Math.min(cursorOffset.start, len), Math.min(cursorOffset.end, len));
    }
    focusEditor();

    switch (type) {
      case "link":
        if (trimmedUrl) {
          const linkText = (text || trimmedUrl).trim() || trimmedUrl;
          insertHTMLAtCursor(
            `<div class="cc-embed cc-embed-link" contenteditable="false">${embedRemoveButton}<a href="${trimmedUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a><span class="cc-embed-link-url">${trimmedUrl}</span></div>`
          );
        }
        break;
      case "image":
        insertHTMLAtCursor(
          `<figure class="cc-embed cc-embed-image" contenteditable="false">${embedRemoveButton}<img src="${trimmedUrl}" alt="" /><figcaption contenteditable="true">Caption</figcaption></figure>`
        );
        break;
      case "audio":
        insertHTMLAtCursor(
          `<div class="cc-embed cc-embed-audio" contenteditable="false">${embedRemoveButton}<audio controls src="${trimmedUrl}">Your browser does not support audio.</audio></div>`
        );
        break;
      case "video":
        insertHTMLAtCursor(
          `<div class="cc-embed cc-embed-video" contenteditable="false">${embedRemoveButton}<video controls src="${trimmedUrl}" style="max-width:100%;height:auto;">Your browser does not support video.</video></div>`
        );
        break;
      default:
        break;
    }
    closeEmbedBar();
  };

  const handleStyleChange = (style) => {
    focusEditor();
    setSelectedStyle(style);
    switch(style) {
      case "Normal text":
        execCommand("formatBlock", "<p>");
        break;
      case "Heading 1":
        execCommand("formatBlock", "<h1>");
        break;
      case "Heading 2":
        execCommand("formatBlock", "<h2>");
        break;
      case "Heading 3":
        execCommand("formatBlock", "<h3>");
        break;
      case "Heading 4":
        execCommand("formatBlock", "<h4>");
        break;
      case "Heading 5":
        execCommand("formatBlock", "<h5>");
        break;
      case "Heading 6":
        execCommand("formatBlock", "<h6>");
        break;
      default:
        break;
    }
  };

  const addTag = () => {
    if (currentTag.trim() && !tags.includes(currentTag.trim())) {
      setTags([...tags, currentTag.trim()]);
      setCurrentTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSaveRevision = async () => {
    try {
      const updateData = {
        title,
        subtitle,
        bodyHtml: debouncedContent,
        tags,
        category: publishCategory,
        socialTitle,
        socialDescription,
        socialImageUrl,
        minorEligible: minorEligible === true,
        updatedAt: new Date().toISOString(),
      };

      if (revisionData) {
        // Update existing revision
        await revisionService.updateRevision(revisionData.id, updateData);
      } else {
        // Create new revision
        const revisionDataToSave = {
          ...updateData,
          bodyHtml: cleanContent, // Use clean content without highlighting
          contentId: contentId,
          author: originalContent?.author,
          authorEmail: originalContent?.authorEmail,
          authorRole: originalContent?.authorRole,
          status: "pending"
        };
        await revisionService.createRevision(revisionDataToSave);
      }

      setSuccessAction("saved");
      setShowSuccessModal(true);
    } catch (error) {
      alert("Failed to save revision. Please try again.");
    }
  };

  const handleApplyRevisions = () => {
    setConfirmAction("apply");
    setShowConfirmModal(true);
  };

  const handleRejectRevisions = () => {
    setConfirmAction("reject");
    setShowConfirmModal(true);
  };

  const executeConfirmAction = async () => {
    try {
      setIsExecutingAction(true);

      if (!revisionData) {
        alert("No revision data found.");
        setIsExecutingAction(false);
        return;
      }

      if (confirmAction === "apply") {
        const updateData = {
          title,
          subtitle,
          bodyHtml: cleanContent, // Use clean content without highlighting
          tags,
          category: publishCategory,
          socialTitle,
          socialDescription,
          socialImageUrl,
          minorEligible: minorEligible === true,
          updatedAt: new Date().toISOString(),
        };

        // Use batch operations for atomic updates
        const batch = writeBatch(db);

        // Update published collection
        if (originalContent?.contentId) {
          const publishedRef = doc(db, 'published', originalContent.contentId);
          batch.set(publishedRef, updateData, { merge: true });
        }

        // Find and update approval document
        const approvalDoc = await approvalService.getApprovalById(revisionData.originalApprovalId);
        if (approvalDoc) {
          const approvalRef = doc(db, 'approvals', approvalDoc.id);
          batch.set(approvalRef, updateData, { merge: true });
        }

        // Delete the revision document
        const revisionRef = doc(db, 'revisions', revisionData.id);
        batch.delete(revisionRef);

        // Commit all operations atomically
        await batch.commit();
        
        // Invalidate caches
        if (originalContent?.contentId) {
          publishedService._clearCache?.();
        }
        revisionService._clearCache?.();

        // Show success modal
        setSuccessAction("applied");
        setShowSuccessModal(true);
        setIsExecutingAction(false);
      } else if (confirmAction === "reject") {
        // Delete the revision document
        await revisionService.deleteRevision(revisionData.id);

        // Show success modal
        setSuccessAction("rejected");
        setShowSuccessModal(true);
        setIsExecutingAction(false);
      }
    } catch (error) {
      alert("Failed to complete the action. Please try again.");
      setIsExecutingAction(false);
    }
  };

  const handleConfirmCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  // Debounce content updates to reduce frequency of effect execution
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedContent(content);
    }, 150); // 150ms debounce delay
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [content]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (historyTimerRef.current) {
        clearTimeout(historyTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    
    const isProgrammatic = contentUpdateSourceRef.current === "programmatic";
    const pendingCursor = cursorRestoreRef.current;

    if (isProgrammatic) {
      editor.innerHTML = debouncedContent || "Start writing...";
    } else if (debouncedContent === "" && (!editor.innerHTML || editor.innerHTML === "Start writing..." || editor.innerHTML === "<br>")) {
      editor.innerHTML = "Start writing...";
    }

    if (isProgrammatic && pendingCursor != null) {
      cursorRestoreRef.current = null;
      const { start, end } = pendingCursor;
      const len = editor.textContent?.length ?? 0;
      requestAnimationFrame(() => {
        const el = editorRef.current;
        if (el && typeof el.nodeType === "number" && document.contains(el)) {
          setCaretPosition(el, Math.min(start, len), Math.min(end, len));
        }
      });
    }
    
    contentUpdateSourceRef.current = null;
  }, [debouncedContent]);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      const handleFocus = () => {
        editor.style.direction = 'ltr';
        editor.style.unicodeBidi = 'embed';
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        const range = selection.getRangeAt(0);
        const startOffset = range.startOffset;
        const startContainer = range.startContainer;
        if (!startContainer || typeof startContainer.nodeType !== "number") return;

        setTimeout(() => {
          try {
            const newRange = document.createRange();
            if (startContainer.nodeType === Node.TEXT_NODE && document.contains(startContainer)) {
              newRange.setStart(startContainer, Math.min(startOffset, startContainer.length));
              newRange.collapse(true);
            } else if (document.contains(editor)) {
              newRange.selectNodeContents(editor);
              newRange.collapse(false);
            } else {
              return;
            }
            selection.removeAllRanges();
            selection.addRange(newRange);
          } catch (e) {
            try {
              if (document.contains(editor)) {
                const fallbackRange = document.createRange();
                fallbackRange.selectNodeContents(editor);
                fallbackRange.collapse(false);
                selection.removeAllRanges();
                selection.addRange(fallbackRange);
              }
            } catch (e2) {
              // ignore
            }
          }
        }, 0);
      };
      
      const handleInput = () => {
        editor.style.direction = 'ltr';
        editor.style.unicodeBidi = 'embed';
      };

      const handleKeyDown = (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Backspace' || e.key === 'Delete') {
          setTimeout(() => {
            editor.style.direction = 'ltr';
            editor.style.unicodeBidi = 'embed';
          }, 0);
        }
      };

      const handleMouseUp = () => {
        setTimeout(() => {
          editor.style.direction = 'ltr';
          editor.style.unicodeBidi = 'embed';
        }, 0);
      };

      editor.addEventListener('focus', handleFocus);
      editor.addEventListener('input', handleInput);
      editor.addEventListener('keydown', handleKeyDown);
      editor.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        editor.removeEventListener('focus', handleFocus);
        editor.removeEventListener('input', handleInput);
        editor.removeEventListener('keydown', handleKeyDown);
        editor.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, []);

  if (loading) {
    return (
      <div className="cc-add-content-container">
        <style>{`
          .admin-aura-skeleton {
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
            border-radius: 4px;
          }
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          .admin-aura-skeleton-text {
            height: 16px;
            margin-bottom: 8px;
          }
          .admin-aura-skeleton-title {
            height: 32px;
            width: 60%;
            margin-bottom: 16px;
          }
          .admin-aura-skeleton-input {
            height: 40px;
            width: 100%;
          }
          .admin-aura-skeleton-editor {
            height: 300px;
            width: 100%;
          }
        `}</style>
        {/* Top Header Bar with System Branding (Fixed) */}
        <header className="cc-top-header" style={{ 
          background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.3)',
          padding: '14px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          border: 'none',
          borderRadius: '0'
        }}>
          <div className="cc-header-left" style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: 0, padding: 0 }}>
            <span style={{ color: '#f8fafc', fontWeight: '600', fontSize: '14px' }}>Reviewing Proposed Revision</span>
          </div>
          <div className="cc-header-right" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            margin: 0,
            padding: 0
          }}>
            <div className="admin-aura-skeleton" style={{ width: '280px', height: '40px', borderRadius: '10px' }} />
            <div className="admin-aura-skeleton" style={{ width: '160px', height: '36px', borderRadius: '8px' }} />
          </div>
        </header>

        {/* Editor Area - Split View (Refined) */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row',
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          gap: '24px', 
          padding: '24px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
          minHeight: 'calc(100vh - 80px)',
          overflowY: 'auto'
        }}>
          {/* Original Content - Read Only */}
          <div style={{ 
            width: 'calc(50% - 12px)',
            minWidth: 0,
            background: '#fff',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
            overflowY: 'auto',
            minHeight: '520px',
            maxHeight: 'none'
          }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
              padding: '16px 20px',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '16px' }}>Original Version (Published)</h3>
            </div>
            <div className="admin-aura-skeleton admin-aura-skeleton-input" style={{ height: '50px', marginBottom: '16px' }} />
            <div className="admin-aura-skeleton admin-aura-skeleton-input" style={{ height: '40px', marginBottom: '20px' }} />
            <div className="admin-aura-skeleton admin-aura-skeleton-editor" />
          </div>

          {/* Proposed Revision - Editable */}
          <div style={{ 
            width: 'calc(50% - 12px)',
            minWidth: 0,
            background: '#fff',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
            overflowY: 'auto',
            minHeight: '520px',
            maxHeight: 'none'
          }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              padding: '16px 20px',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '16px' }}>Proposed Revision</h3>
            </div>
            <div className="admin-aura-skeleton admin-aura-skeleton-input" style={{ height: '50px', marginBottom: '16px' }} />
            <div className="admin-aura-skeleton admin-aura-skeleton-input" style={{ height: '40px', marginBottom: '20px' }} />
            <div className="admin-aura-skeleton admin-aura-skeleton-editor" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cc-add-content-container">
      {/* Top Header Bar with System Branding (Fixed) */}
      <header className="cc-top-header" style={{ 
        background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
        boxShadow: '0 4px 20px rgba(15, 23, 42, 0.3)',
        padding: '14px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '24px',
        border: 'none',
        borderRadius: '0'
      }}>
        <div className="cc-header-left" style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: 0, padding: 0 }}>
          <button className="cc-back-btn" 
            onClick={() => navigate(`/admin/content-review/${originalContent?.contentId || contentId}`)}
            style={{ 
              background: 'rgba(255,255,255,0.15)', 
              color: '#fff', 
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0
            }}
          >
            <FiArrowLeft style={{ fontSize: '16px' }} />
          </button>
          <div className="cc-saved-status" style={{ 
            cursor: 'default', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            padding: '8px 14px',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.12)',
            margin: 0
          }}>
            <div className="cc-status-dot saved" style={{ 
              background: '#34d399', 
              width: '8px', 
              height: '8px', 
              borderRadius: '50%',
              margin: 0
            }}></div>
            <span style={{ 
              color: '#f8fafc', 
              fontWeight: '600', 
              fontSize: '14px'
            }}>Reviewing Proposed Revision</span>
          </div>
          
          <div className="cc-header-right" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            margin: 0,
            padding: 0
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.06)',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <span style={{ 
                color: characterCount >= 5000 ? '#ef4444' : (characterCount >= 1400 ? '#f59e0b' : '#e2e8f0'), 
                fontWeight: '600', 
                fontSize: '14px' 
              }}>{characterCount} / 5000 characters</span>
            </div>
          </div>
        </div>
        
        <div className="cc-header-right" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          margin: 0,
          padding: 0
        }}>
          <div className="cc-publish-section" style={{ 
            display: 'flex', 
            alignItems: 'center',
            margin: 0,
            padding: 0
          }}>
            <select
              className="cc-publish-select"
              value={publishCategory}
              onChange={(e) => setPublishCategory(e.target.value)}
              style={{
                width: '280px',
                background: 'rgba(255,255,255,0.1)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '10px',
                padding: '10px 14px',
                fontSize: '14px'
              }}
            >
              <option value="" style={{ background: '#0f172a', color: '#fff' }}>Select one category</option>
              {PUBLISH_CATEGORIES.map((c) => (
                <option key={c} value={c} style={{ background: '#0f172a', color: '#fff' }}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <button
            className="cc-preview-btn"
            type="button"
            onClick={() => {
              setPreviewDevice("desktop");
              setIsPreviewOpen(true);
            }}
            style={{ 
              background: 'rgba(255,255,255,0.15)', 
              color: '#fff', 
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              margin: 0,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
              e.target.style.borderColor = 'transparent';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.15)';
              e.target.style.borderColor = 'rgba(255,255,255,0.2)';
            }}
          >
            Preview
          </button>
          <button
            className="cc-preview-btn"
            type="button"
            onClick={() => setIsEditSocialOpen(true)}
            style={{ 
              background: 'rgba(255,255,255,0.15)', 
              color: '#fff', 
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              margin: 0,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
              e.target.style.borderColor = 'transparent';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.15)';
              e.target.style.borderColor = 'rgba(255,255,255,0.2)';
            }}
          >
            Social Preview
          </button>
          <button 
                type="button" 
                onClick={handleRejectRevisions}
                style={{ 
                  background: 'rgba(255,255,255,0.15)', 
                  color: '#fff', 
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  margin: 0,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                  e.target.style.borderColor = 'transparent';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.15)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                }}
              >
                Reject Revisions
              </button>
              <button 
                type="button" 
                onClick={handleApplyRevisions}
                style={{ 
                  background: 'rgba(255,255,255,0.15)', 
                  color: '#fff', 
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  margin: 0,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                  e.target.style.borderColor = 'transparent';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.15)';
                  e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                }}
              >
                Apply Revisions
              </button>
        </div>
      </header>

      {/* Toolbar */}
      <div className="cc-toolbar-header">
        <div className="cc-history-buttons">
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={handleUndo} disabled={historyIndex <= 0}>
            <FiRotateCcw />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={handleRedo} disabled={historyIndex >= history.length - 1}>
            <FiRotateCw />
          </button>
        </div>
        
        <div className="cc-editor-toolbar">
          {/* Style Dropdown */}
          <div className="cc-dropdown">
            <button className="cc-dropdown-btn" onMouseDown={(e) => e.preventDefault()}>
              {selectedStyle} <FiChevronDown size={12} />
            </button>
            <div className="cc-dropdown-menu">
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Normal text")}>Normal text</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 1")}>Heading 1</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 2")}>Heading 2</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 3")}>Heading 3</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 4")}>Heading 4</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 5")}>Heading 5</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleStyleChange("Heading 6")}>Heading 6</div>
            </div>
          </div>
          
          <div className="cc-toolbar-divider"></div>
          
          {/* Text Formatting */}
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('bold')}>
            <FiBold />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('italic')}>
            <FiItalic />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('underline')}>
            <FiUnderline />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('strikeThrough')}>
            <FiMinus />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('formatBlock', '<pre>')}>
            <FiCode />
          </button>
          
          <div className="cc-toolbar-divider"></div>
          
          {/* Media and Links */}
          <button
            className="cc-tool-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openEmbedBar('link')}
            title="Insert link"
          >
            <FiLink />
          </button>
          <button
            className="cc-tool-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openEmbedBar('image')}
            title="Insert image"
          >
            <FiImage />
          </button>
          <button
            className="cc-tool-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openEmbedBar('audio')}
            title="Insert audio"
          >
            <FiVolume2 />
          </button>
          <button
            className="cc-tool-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => openEmbedBar('video')}
            title="Insert video"
          >
            <FiVideo />
          </button>
          <input
            ref={importFileInputRef}
            type="file"
            accept="image/*,audio/*,video/*"
            multiple
            className="cc-import-file-input"
            onChange={handleImportFiles}
            aria-label="Import image, audio, or video from device"
          />
          <button
            type="button"
            className="cc-tool-btn cc-import-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => importFileInputRef.current?.click()}
            title="Import image, audio, or video from device"
          >
            <FiUpload /> Import
          </button>
          
          <div className="cc-toolbar-divider"></div>
          
          {/* Lists */}
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('insertUnorderedList')} title="Bullet list">
            <FiList />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('insertOrderedList')} title="Numbered list">
            <FiList style={{ transform: 'rotate(90deg)' }} />
          </button>
          <button className="cc-tool-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => execCommand('indent')} title="Indent">
            <FiAlignLeft />
          </button>
          
          <div className="cc-toolbar-divider"></div>
          
          {/* Alignment */}
          <div className="cc-dropdown">
            <button className="cc-dropdown-btn" title="Text alignment" onMouseDown={(e) => e.preventDefault()}>
              Align <FiChevronDown size={12} />
            </button>
            <div className="cc-dropdown-menu">
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlignment("left")}><FiAlignLeft /> Left</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlignment("center")}><FiAlignCenter /> Center</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlignment("right")}><FiAlignRight /> Right</div>
              <div onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlignment("justify")}><FiAlignJustify /> Justify</div>
            </div>
          </div>
          
          <div className="cc-toolbar-divider"></div>
          
          {/* Minor Eligible Checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 8px' }}>
            <input
              type="checkbox"
              id="minor-eligible-checkbox"
              checked={minorEligible === true}
              onChange={(e) => setMinorEligible(e.target.checked)}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            />
            <label htmlFor="minor-eligible-checkbox" style={{ cursor: 'pointer', fontSize: '14px', color: '#fff', userSelect: 'none' }}>
              Minor Eligible Content
            </label>
          </div>
          
        </div>
      </div>

      {/* Embed bar – sits below toolbar; disappears on Cancel, media inserts at saved cursor position */}
      {embedBar && (
        <div className="cc-embed-bar">
          {embedBar.type === "link" && (
            <>
              <input
                type="url"
                className="cc-embed-input"
                placeholder="URL"
                value={embedBar.url}
                onChange={(e) => setEmbedBar((b) => ({ ...b, url: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && insertEmbed()}
              />
              <input
                type="text"
                className="cc-embed-input"
                placeholder="Link text (optional)"
                value={embedBar.text}
                onChange={(e) => setEmbedBar((b) => ({ ...b, text: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && insertEmbed()}
              />
            </>
          )}
          {(embedBar.type === "image" || embedBar.type === "audio" || embedBar.type === "video") && (
            <input
              type="url"
              className="cc-embed-input cc-embed-input-wide"
              placeholder={`Enter ${embedBar.type} URL`}
              value={embedBar.url}
              onChange={(e) => setEmbedBar((b) => ({ ...b, url: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && insertEmbed()}
            />
          )}
          {embedBar.type === "comment" && (
            <span className="cc-embed-hint">Click Insert to add a comment block in the content.</span>
          )}
          <div className="cc-embed-bar-actions">
            <button type="button" className="cc-btn cc-embed-insert" onClick={insertEmbed}>
              Insert
            </button>
            <button type="button" className="cc-btn cc-embed-cancel" onClick={closeEmbedBar}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Editor Area - Split View (Refined) */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'row',
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto',
        gap: '24px', 
        padding: '24px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
        minHeight: 'calc(100vh - 80px)',
        overflowY: 'auto'
      }}>
        {/* Original Content - Read Only */}
        <div style={{ 
          width: 'calc(50% - 12px)',
          minWidth: 0,
          background: '#fff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0',
          overflowY: 'auto',
          minHeight: '520px',
          maxHeight: 'none'
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '16px' }}>Original Version (Published)</h3>
          </div>
          
          <input
            type="text"
            className="cc-title"
            placeholder="Title"
            value={originalContent?.title || ""}
            readOnly
            style={{ 
              backgroundColor: '#f8fafc',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              padding: '12px 16px',
              fontSize: '20px',
              fontWeight: 700,
              width: '100%',
              marginBottom: '16px'
            }}
          />
          
          <input
            type="text"
            className="cc-subtitle"
            placeholder="Add a subtitle..."
            value={originalContent?.subtitle || ""}
            readOnly
            style={{ 
              backgroundColor: '#f8fafc',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              padding: '10px 14px',
              fontSize: '16px',
              width: '100%',
              marginBottom: '20px'
            }}
          />
          
          <div
            className="cc-text-editor"
            style={{ 
              backgroundColor: '#f8fafc', 
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              overflowY: 'auto',
              minHeight: '300px'
            }}
            dangerouslySetInnerHTML={{ __html: originalContent?.bodyHtml || "" }}
          />
        </div>

        {/* Proposed Revision - Editable */}
        <div style={{ 
          width: 'calc(50% - 12px)',
          minWidth: 0,
          background: '#fff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0',
          overflowY: 'auto',
          minHeight: '520px',
          maxHeight: 'none'
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '16px' }}>Proposed Revision</h3>
          </div>
          
          <div style={{ 
            marginBottom: '16px',
            padding: '12px',
            background: '#f0f9ff',
            borderRadius: '8px',
            border: '1px solid #bae6fd',
            fontSize: '13px',
            color: '#0369a1'
          }}>
            Changes from original are highlighted in light blue
          </div>
          
          <input
            type="text"
            className="cc-title"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            aria-label="Content title"
            style={{ 
              backgroundColor: '#f0fdf4',
              borderRadius: '10px',
              border: '1px solid #bbf7d0',
              padding: '12px 16px',
              fontSize: '20px',
              fontWeight: 700,
              width: '100%',
              marginBottom: '16px'
            }}
          />
          
          <input
            type="text"
            className="cc-subtitle"
            placeholder="Add a subtitle..."
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            aria-label="Content subtitle"
            style={{ 
              backgroundColor: '#f0fdf4',
              borderRadius: '10px',
              border: '1px solid #bbf7d0',
              padding: '10px 14px',
              fontSize: '16px',
              width: '100%',
              marginBottom: '20px'
            }}
          />
          
          <div
            ref={(el) => {
              editorRef.current = el;
              if (el && !el.innerHTML) {
                el.innerHTML = debouncedContent || "Start writing...";
              }
            }}
            className="cc-text-editor"
            contentEditable
            onInput={handleContentChange}
            onClick={handleEditorClick}
            suppressContentEditableWarning={true}
            style={{ 
              backgroundColor: '#f8fafc', 
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              overflowY: 'auto',
              minHeight: '300px'
            }}
          />
        </div>
      </div>

      {/* Live Preview Modal with System Branding */}
      {isPreviewOpen && (
        <Portal>
          <div className="cc-preview-overlay" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
          <div className="cc-preview-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            <header className="cc-preview-header" style={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
              color: '#fff',
              padding: '24px 32px'
            }}>
              <div>
                <div className="cc-edit-social-title" style={{ color: '#fff' }}>Preview</div>
                <div className="cc-edit-social-subtitle" style={{ color: '#cbd5e1' }}>
                  See how your content will appear on different devices
                </div>
              </div>
              <button
                type="button"
                className="cc-edit-social-close"
                onClick={() => setIsPreviewOpen(false)}
                aria-label="Close preview"
                title="Close"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  borderRadius: '10px'
                }}
              >
                <FiX />
              </button>
            </header>

            <div style={{
              padding: '16px 32px',
              background: 'rgba(15, 23, 42, 0.5)',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div className="cc-preview-tabs" style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className={previewDevice === "mobile" ? "active" : ""}
                  onClick={() => setPreviewDevice("mobile")}
                  style={{
                    color: '#fff',
                    background: previewDevice === "mobile" ? 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)' : 'transparent',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Mobile
                </button>
                <button
                  type="button"
                  className={previewDevice === "desktop" ? "active" : ""}
                  onClick={() => setPreviewDevice("desktop")}
                  style={{
                    color: '#fff',
                    background: previewDevice === "desktop" ? 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)' : 'transparent',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Desktop
                </button>
              </div>
            </div>

            <div className="cc-preview-body">
              {previewDevice === "mobile" && (
                <div className="cc-preview-mobile-frame">
                  <div className="cc-preview-mobile-notch" />
                  <div className="cc-preview-mobile-content">
                    <h1>{title || "Article Title"}</h1>
                    {subtitle && <h2>{subtitle}</h2>}
                    <div dangerouslySetInnerHTML={{ __html: debouncedContent }} />
                  </div>
                </div>
              )}
              {previewDevice === "desktop" && (
                <div className="cc-preview-desktop-content">
                  <h1>{title || "Article Title"}</h1>
                  {subtitle && <h2>{subtitle}</h2>}
                  <div dangerouslySetInnerHTML={{ __html: debouncedContent }} />
                </div>
              )}
            </div>
          </div>
        </div>
        </Portal>
      )}

      {/* Edit social preview modal with System Branding */}
      {isEditSocialOpen && (
        <Portal>
          <div className="cc-edit-social-overlay" role="dialog" aria-modal="true" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
          <div className="cc-edit-social-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            <header className="cc-edit-social-header" style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
              color: '#fff'
            }}>
              <div>
                <div className="cc-edit-social-title" style={{ color: '#fff' }}>Edit social preview</div>
                <div className="cc-edit-social-subtitle" style={{ color: '#cbd5e1' }}>
                  Change how this post appears on other platforms.
                </div>
              </div>
              <button
                type="button"
                className="cc-edit-social-close"
                onClick={closeEditSocial}
                aria-label="Close edit social preview"
                title="Close"
                style={{ 
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  borderRadius: '10px'
                }}
              >
                <FiX />
              </button>
            </header>

            <div className="cc-edit-social-body">
              <label className="cc-edit-field">
                <span>Title</span>
                <input
                  type="text"
                  value={socialTitle}
                  onChange={(e) => setSocialTitle(e.target.value)}
                  placeholder="Title"
                />
              </label>

              <label className="cc-edit-field">
                <span>Description</span>
                <textarea
                  value={socialDescription}
                  onChange={(e) => setSocialDescription(e.target.value)}
                  placeholder="Description"
                  rows={4}
                  maxLength={250}
                />
                <div className="cc-edit-count">
                  {socialDescription.length} / 250 (Recommended)
                </div>
              </label>

              <div className="cc-edit-image-section">
                <div className="cc-edit-image-label">Image</div>
                <div
                  className="cc-edit-image-box"
                  role="button"
                  tabIndex={0}
                  onClick={() => socialImageInputRef.current?.click()}
                  onKeyDown={(e) =>
                    e.key === "Enter" && socialImageInputRef.current?.click()
                  }
                  title="Click to import image"
                >
                  {socialImageUrl ? (
                    <>
                      <img src={socialImageUrl} alt="" />
                      <button
                        type="button"
                        className="cc-edit-image-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSocialImageUrl("");
                        }}
                        aria-label="Remove image"
                        title="Remove image"
                      >
                        <FiX />
                      </button>
                    </>
                  ) : (
                    <div className="cc-edit-image-placeholder">
                      Click to import an image
                    </div>
                  )}
                  <input
                    ref={socialImageInputRef}
                    type="file"
                    accept="image/*"
                    className="cc-import-file-input"
                    onChange={handlePickSocialImage}
                  />
                </div>
              </div>
            </div>

            <footer className="cc-edit-social-footer">
              <button type="button"
                className="cc-edit-cancel"
                onClick={closeEditSocial}
                style={{
                  background: '#f1f5f9',
                  color: '#475569',
                  borderRadius: '10px',
                  border: 'none',
                  padding: '12px 24px',
                  fontWeight: 600,
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button type="button"
                className="cc-edit-save"
                onClick={closeEditSocial}
                style={{
                  background: '#f1f5f9',
                  color: '#475569',
                  borderRadius: '10px',
                  border: 'none',
                  padding: '12px 24px',
                  fontWeight: 600,
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                  e.target.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f1f5f9';
                  e.target.style.color = '#475569';
                }}
              >
                Save
              </button>
            </footer>
          </div>
        </div>
        </Portal>
      )}

      {/* Confirmation Modal with System Branding */}
      {showConfirmModal && (
        <Portal>
          <div className="cc-edit-social-overlay" role="dialog" aria-modal="true" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
          <div className="cc-edit-social-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            <header className="cc-edit-social-header" style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
              color: '#fff'
            }}>
              <div>
                <div className="cc-edit-social-title" style={{ color: '#fff' }}>
                  {confirmAction === "apply" ? "Apply Revisions?" : "Reject Revisions?"}
                </div>
                <div className="cc-edit-social-subtitle" style={{ color: '#cbd5e1' }}>
                  {confirmAction === "apply" 
                    ? "Are you sure you want to apply these revisions to the published content?" 
                    : "Are you sure you want to reject these proposed revisions?"}
                </div>
              </div>
              <button
                type="button"
                className="cc-edit-social-close"
                onClick={handleConfirmCancel}
                aria-label="Close confirmation modal"
                title="Close"
                style={{ 
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  borderRadius: '10px'
                }}
              >
                <FiX />
              </button>
            </header>
            <footer className="cc-edit-social-footer">
              <button type="button"
                className="cc-edit-cancel"
                onClick={handleConfirmCancel}
                style={{
                  background: '#f1f5f9',
                  color: '#475569',
                  borderRadius: '10px',
                  border: 'none'
                }}
              >
                Cancel
              </button>
              <button type="button"
                className="cc-edit-save"
                onClick={executeConfirmAction}
                disabled={isExecutingAction}
                style={{
                  background: '#f1f5f9',
                  color: '#475569',
                  borderRadius: '10px',
                  border: 'none',
                  transition: 'all 0.2s ease',
                  opacity: isExecutingAction ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isExecutingAction) {
                    e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                    e.target.style.color = '#fff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isExecutingAction) {
                    e.target.style.background = '#f1f5f9';
                    e.target.style.color = '#475569';
                  }
                }}
              >
                {isExecutingAction
                  ? (confirmAction === "apply" ? "Applying..." : "Rejecting...")
                  : (confirmAction === "apply" ? "Apply Revisions" : "Reject Revisions")
                }
              </button>
            </footer>
          </div>
        </div>
        </Portal>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <Portal>
          <div className="cc-success-overlay" role="dialog" aria-modal="true" style={{ background: 'rgba(15, 23, 42, 0.85)' }}>
            <div className="cc-success-modal" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <header className="cc-success-header" style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
                color: '#fff',
                padding: '24px 32px'
              }}>
                <div className="cc-success-icon" style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '2px solid rgba(255,255,255,0.3)'
                }}>
                  <div className="cc-success-checkmark" style={{ color: '#fff' }}>✓</div>
                </div>
                <div className="cc-success-title" style={{ color: '#fff' }}>
                  {successAction === 'applied' ? 'Revisions Applied Successfully!' :
                   successAction === 'rejected' ? 'Revisions Rejected Successfully!' :
                   'Revision Saved Successfully!'}
                </div>
              </header>

              <div className="cc-success-content">
                <p>
                  {successAction === 'applied'
                    ? 'The proposed revisions have been successfully applied.'
                    : successAction === 'rejected'
                      ? 'The proposed revisions have been rejected.'
                      : 'Your revision has been successfully saved.'}
                </p>
                <p>The changes will now be visible in the content management dashboard.</p>
              </div>

              <footer className="cc-success-footer">
                <button
                  type="button"
                  className="cc-success-btn"
                  onClick={() => {
                    setShowSuccessModal(false);
                    if (successAction === 'applied' || successAction === 'rejected') {
                      navigate(`/admin/content-review/${revisionData?.originalApprovalId || originalContent?.contentId}`);
                    } else {
                      navigate(`/article-view?id=${revisionData?.originalApprovalId || originalContent?.contentId || contentId}`);
                    }
                  }}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    borderRadius: '10px',
                    border: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                    e.target.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f1f5f9';
                    e.target.style.color = '#475569';
                  }}
                >
                  {successAction === 'applied' || successAction === 'rejected' ? 'Back to Content Review' : 'Back to Article View'}
                </button>
              </footer>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default AdminContentRevision;
